/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable react/require-default-props */
/* eslint-disable react/react-in-jsx-scope */
import { useState, useEffect, useRef } from 'react';
import { debounce } from 'lodash';
import { Cross } from '@saleshandy/icons';
import classNames from 'classnames';
import { SkeletonLoading } from '@saleshandy/design-system';
import { Spinner as Loader } from 'react-bootstrap';
import InfiniteScroll from 'react-infinite-scroll-component';
import {
  FilterComponentType,
  GetV2FilterSuggestionResponse,
} from '../../types';
import { getV2FilterSuggestionAPI } from '../../helpers/leads.api';
import {
  getSuggestionValue,
  getSuggestionKey,
  isV2Suggestion,
  resolveFlagImageIso2,
} from './checkbox-filter/v2-filter-checkbox.helpers';
import TruncatedTextWithAutoTooltip from '../../../../shared/components/truncated-text-with-auto-tooltip';
import DropDownFilterComponent from './v2-dropdown-filter';
import { getCountryFlagImageUrl } from '../../../../shared/components/country-code-phone-input-picker/country-flag-image';

type SuggestionItem = string | { value: string; type: string; emoji: string };

interface AddedRow {
  paramIndex: number;
  countryLabel: string;
  countryValue: string;
}

interface GroupDropdownWithSuggestionsFilterProps {
  subFilter: {
    key: string;
    rangeList: any[];
    placeholder?: string;
    suggestionApi: { type: string; paramType?: string };
  };
  handleQuery: (params: {
    filterKey: string;
    filterValue: any;
    filterType: string;
  }) => void;
  pendingFilters: URLSearchParams;
  applyPendingFilters?: (filters?: URLSearchParams) => void;
  isExpand?: boolean;
}

const GroupDropdownWithSuggestionsFilter = ({
  subFilter,
  handleQuery,
  pendingFilters,
  applyPendingFilters,
  isExpand = false,
}: GroupDropdownWithSuggestionsFilterProps) => {
  const {
    key: filterKey,
    rangeList = [],
    placeholder = 'Select country',
    suggestionApi,
  } = subFilter;

  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState<SuggestionItem[]>([]);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [addedRows, setAddedRows] = useState<AddedRow[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const searchTermRef = useRef<string>('');
  const nextParamIndexRef = useRef(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const baseCountryKey = `${filterKey}`;
  const basePercentageKey = `${filterKey}_percentage`;

  // Hydrate addedRows from pendingFilters (on mount and when filters change e.g. clear all / individual tag removal)
  useEffect(() => {
    const rows: AddedRow[] = [];
    const seenIndices = new Set<number>();

    pendingFilters.forEach((value, paramKey) => {
      if (
        paramKey.startsWith(baseCountryKey) &&
        !paramKey.includes('_percentage_')
      ) {
        const match = paramKey.match(
          new RegExp(
            `^${baseCountryKey.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}_(\\d+)$`,
          ),
        );
        if (match) {
          const index = parseInt(match[1], 10);
          if (
            !seenIndices.has(index) &&
            value &&
            value !== '' &&
            value !== 'select'
          ) {
            seenIndices.add(index);
            rows.push({
              paramIndex: index,
              countryLabel: value,
              countryValue: value,
            });
            if (index >= nextParamIndexRef.current) {
              nextParamIndexRef.current = index + 1;
            }
          }
        }
      }
    });

    rows.sort((a, b) => a.paramIndex - b.paramIndex);
    setAddedRows(rows);
  }, [pendingFilters.toString(), baseCountryKey]);

  const handleInputChange = async (search: string, page = 1) => {
    try {
      if (search.trim() === '') {
        setIsLoading(false);
        setShowAutocomplete(false);
        setSelectedSuggestionIndex(-1);
        return;
      }

      searchTermRef.current = search;
      setIsLoading(true);
      setShowAutocomplete(true);

      const payload = {
        type: suggestionApi.type,
        search,
        paramType: suggestionApi.paramType,
        page,
      };
      const res: GetV2FilterSuggestionResponse = await getV2FilterSuggestionAPI(
        payload,
      );

      // Discard stale responses
      if (searchTermRef.current !== search) return;

      setIsLoading(false);
      if (res?.payload && search.trim() !== '') {
        const responseData = res.payload;
        let newSuggestions: SuggestionItem[];
        let newHasMore = false;

        if (
          responseData &&
          typeof responseData === 'object' &&
          'data' in responseData
        ) {
          const paginated = (responseData as unknown) as {
            data: SuggestionItem[];
            hasMore: boolean;
          };
          newSuggestions = paginated.data;
          newHasMore = paginated.hasMore;
        } else if (Array.isArray(responseData)) {
          newSuggestions = responseData;
        } else {
          newSuggestions = [];
        }

        if (page === 1) {
          setSuggestions(newSuggestions);
        } else {
          setSuggestions((prev) => [...prev, ...newSuggestions]);
        }
        setHasMore(newHasMore);
        setSelectedSuggestionIndex(-1);
      } else {
        setSuggestions([]);
      }
    } catch (e) {
      setIsLoading(false);
      setShowAutocomplete(false);
    }
  };

  const fetchNextPage = () => {
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    handleInputChange(searchTermRef.current || inputValue, nextPage);
  };

  const debouncedInputChangeRef = useRef<ReturnType<typeof debounce>>();
  useEffect(() => {
    debouncedInputChangeRef.current = debounce(handleInputChange, 500);
    return () => {
      debouncedInputChangeRef.current?.cancel();
    };
  }, [filterKey, suggestionApi?.type, suggestionApi?.paramType]);

  useEffect(() => {
    if (isExpand && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isExpand]);

  const onSelectSuggestion = (countryLabel: string, countryValue: string) => {
    const paramIndex = nextParamIndexRef.current;
    nextParamIndexRef.current += 1;

    setAddedRows((prev) => [
      ...prev,
      { paramIndex, countryLabel, countryValue },
    ]);
    handleQuery({
      filterKey: `${baseCountryKey}_${paramIndex}`,
      filterValue: countryValue,
      filterType: FilterComponentType.DROP_DOWN,
    });
    setInputValue('');
    setSuggestions([]);
    setShowAutocomplete(false);
    setSelectedSuggestionIndex(-1);
  };

  const handleRemoveRow = (row: AddedRow) => {
    setAddedRows((prev) => prev.filter((r) => r.paramIndex !== row.paramIndex));
    handleQuery({
      filterKey: `${basePercentageKey}_${row.paramIndex}`,
      filterValue: '',
      filterType: FilterComponentType.DROP_DOWN,
    });
    handleQuery({
      filterKey: `${baseCountryKey}_${row.paramIndex}`,
      filterValue: '',
      filterType: FilterComponentType.DROP_DOWN,
    });
  };

  const skeletonCount = 5;

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const direction = e.key === 'ArrowDown' ? 1 : -1;
      const newIndex = selectedSuggestionIndex + direction;
      if (newIndex >= 0 && newIndex < suggestions?.length) {
        setSelectedSuggestionIndex(newIndex);
      }
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (
        suggestions?.length > 0 &&
        selectedSuggestionIndex >= 0 &&
        selectedSuggestionIndex < suggestions.length
      ) {
        const selected = suggestions[selectedSuggestionIndex];
        const value = getSuggestionValue(selected as any);
        onSelectSuggestion(value, value);
      }
    }
  };

  return (
    <div className="group-dropdown-filter group-dropdown-with-suggestions">
      <div className="group-selector position-relative">
        <input
          ref={inputRef}
          type="text"
          className="auto-search-input dropdown-filter-select"
          placeholder={placeholder}
          value={inputValue}
          onChange={(e) => {
            const v = e.target.value;
            setInputValue(v);
            setCurrentPage(1);
            setHasMore(false);
            debouncedInputChangeRef.current?.(v);
          }}
          onFocus={() => inputValue.trim() && setShowAutocomplete(true)}
          onBlur={() => {
            setTimeout(() => setShowAutocomplete(false), 200);
          }}
          onKeyDown={onKeyDown}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={showAutocomplete}
          aria-controls={
            showAutocomplete ? `${filterKey}-country-listbox` : undefined
          }
        />
        {showAutocomplete && (
          <ul
            id={`${filterKey}-country-listbox`}
            role="listbox"
            className={classNames('autocomplete', {
              'autocomplete-loading': isLoading && suggestions?.length === 0,
            })}
          >
            {isLoading &&
              suggestions?.length === 0 &&
              Array.from({ length: skeletonCount }, (_, index) => (
                <li
                  key={`skeleton-loading-${filterKey}-${index}`}
                  className="item d-flex align-items-center position-relative justify-content-between"
                >
                  <div className="d-flex flex-column w-100">
                    <SkeletonLoading
                      width="100%"
                      height={40}
                      borderRadius={4}
                    />
                  </div>
                </li>
              ))}
            {!isLoading && suggestions?.length === 0 && (
              <li className="item d-flex align-items-center justify-content-center">
                <span className="regular-2 gray-txt-11">No match found</span>
              </li>
            )}
            {!(isLoading && suggestions?.length === 0) && (
              <InfiniteScroll
                dataLength={suggestions?.length || 0}
                hasMore={hasMore}
                next={fetchNextPage}
                loader={
                  <div className="d-flex justify-content-center align-items-center py-2">
                    <Loader
                      animation="border"
                      size="sm"
                      className="spinner--blue"
                    />
                  </div>
                }
                endMessage={
                  suggestions?.length > 0 ? (
                    <div className="autocomplete-end-message d-flex justify-content-center align-items-center py-2">
                      <p className="autocomplete-end-text mb-0">The End</p>
                    </div>
                  ) : (
                    <span />
                  )
                }
                scrollableTarget={`${filterKey}-country-listbox`}
              >
                {suggestions?.map((suggestion, index) => {
                  const suggestionValue = getSuggestionValue(suggestion as any);
                  const suggestionKey = getSuggestionKey(
                    suggestion as any,
                    index,
                  );
                  const isSelected = index === selectedSuggestionIndex;
                  const isV2 = isV2Suggestion(suggestion as any);
                  const v2EmojiStr =
                    isV2 && (suggestion as any).emoji
                      ? String((suggestion as any).emoji)
                      : '';
                  const v2FlagIso2 = v2EmojiStr
                    ? resolveFlagImageIso2(v2EmojiStr)
                    : null;
                  const v2FlagImageSrc = v2FlagIso2
                    ? getCountryFlagImageUrl(v2FlagIso2)
                    : '';

                  return (
                    <li
                      key={suggestionKey}
                      role="option"
                      aria-selected={isSelected}
                    >
                      <div
                        className={classNames(
                          'item d-flex align-items-center position-relative justify-content-between w-100',
                          { 'autocomplete-menu-list': isSelected },
                        )}
                        onMouseDown={(e) => {
                          // Prevent input blur before click handler runs.
                          e.preventDefault();
                        }}
                        onClick={() =>
                          onSelectSuggestion(suggestionValue, suggestionValue)
                        }
                      >
                        <div className="d-flex flex-column">
                          <div className="d-flex align-items-center">
                            {isV2 && (suggestion as any).emoji && (
                              <span className="autocomplete-emoji mr-2">
                                {v2FlagImageSrc ? (
                                  <img
                                    src={v2FlagImageSrc}
                                    alt=""
                                    width={22}
                                    height={16}
                                    className="v2-location-flag-img"
                                    loading="lazy"
                                    decoding="async"
                                    onError={(e) => {
                                      e.currentTarget.style.display = 'none';
                                    }}
                                  />
                                ) : (
                                  (suggestion as any).emoji
                                )}
                              </span>
                            )}
                            <TruncatedTextWithAutoTooltip
                              className="autocomplete-text"
                              text={suggestionValue}
                            />
                          </div>
                          {isV2 && (suggestion as any).type && (
                            <p className="autocomplete-type mb-0 gray-txt-11">
                              {(suggestion as any).type}
                            </p>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </InfiniteScroll>
            )}
          </ul>
        )}
      </div>

      {addedRows.map((row) => (
        <div key={row.paramIndex} className="group-dropdown-row">
          <div className="group-row-header">
            <TruncatedTextWithAutoTooltip
              className="group-row-label"
              text={row.countryLabel}
            />
            <button
              type="button"
              className="group-row-remove"
              onClick={() => handleRemoveRow(row)}
              aria-label={`Remove ${row.countryLabel}`}
            >
              <Cross width={16} height={16} />
            </button>
          </div>
          <div className="group-row-dropdown">
            <DropDownFilterComponent
              subFilter={{
                key: `${basePercentageKey}_${row.paramIndex}`,
                list: rangeList,
                name: '',
              }}
              handleQuery={handleQuery}
              isDisabledByUnknown={false}
              disabledByDependencyTooltip={undefined}
              pendingFilters={pendingFilters}
              applyPendingFilters={applyPendingFilters}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

export default GroupDropdownWithSuggestionsFilter;
